import { useEffect } from 'react';

import { trackEvent } from '~/features/analytics/track-event';

type CatalogSearchSurface = 'catalog_page' | 'header_dropdown';
export type CatalogSearchOrigin = 'catalog_inline_search' | 'header_search' | 'url';

type CatalogSearchSource =
  | { searchSurface: 'header_dropdown'; searchOrigin?: never }
  | { searchSurface: 'catalog_page'; searchOrigin?: CatalogSearchOrigin };

const CROSS_SURFACE_DEDUPE_WINDOW_MS = 10_000;
let recentCompletion: { searchTerm: string; searchSurface: CatalogSearchSurface; trackedAt: number } | undefined;
// Search surfaces unmount (mobile drawer close, catalog back-navigation), so the last tracked term must outlive the component.
// Catalog terms are scoped to the Next history entry key: back/forward restores it, a new search push creates a new one,
// and catalog refinements use router.replace, which keeps it.
const lastTrackedSearchTerms = new Map<string, string>();

function consumeRecentCrossSurfaceCompletion(searchTerm: string, searchSurface: CatalogSearchSurface) {
  const completion = recentCompletion;
  if (!completion || completion.searchSurface === searchSurface) {
    return false;
  }
  recentCompletion = undefined;

  return completion.searchTerm === searchTerm && Date.now() - completion.trackedAt <= CROSS_SURFACE_DEDUPE_WINDOW_MS;
}

export function useTrackCatalogSearchCompleted({
  searchTerm,
  resultCount,
  searchSurface,
  searchOrigin,
  isSettled,
  isVisible = true,
}: {
  searchTerm: unknown;
  resultCount: number | undefined;
  isSettled: boolean;
  isVisible?: boolean;
} & CatalogSearchSource) {
  useEffect(() => {
    const trimmedSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : undefined;
    const trackingKey =
      searchSurface === 'catalog_page' ? `${searchSurface}:${window.history.state?.key}` : searchSurface;

    if (!trimmedSearchTerm) {
      lastTrackedSearchTerms.delete(trackingKey);
      return;
    }

    if (
      !isVisible ||
      !isSettled ||
      resultCount === undefined ||
      lastTrackedSearchTerms.get(trackingKey) === trimmedSearchTerm
    ) {
      return;
    }

    lastTrackedSearchTerms.set(trackingKey, trimmedSearchTerm);

    // Header and catalog requests can settle in either order during client-side navigation.
    // If navigation becomes a full document load, carry an explicit search ID instead.
    if (consumeRecentCrossSurfaceCompletion(trimmedSearchTerm, searchSurface)) {
      return;
    }

    recentCompletion = { searchTerm: trimmedSearchTerm, searchSurface, trackedAt: Date.now() };

    trackEvent({
      name: 'Catalog Search Completed',
      type: 'search',
      label: 'Catalog Search',
      action: 'catalog_search_completed',
      value: trimmedSearchTerm,
      additionalAttributes: {
        search_term: trimmedSearchTerm,
        result_count: resultCount,
        has_results: resultCount > 0,
        search_surface: searchSurface,
        ...(searchOrigin ? { search_origin: searchOrigin } : {}),
      },
    });
  }, [isSettled, isVisible, resultCount, searchOrigin, searchSurface, searchTerm]);
}
